import React, { useState, useRef, useEffect, useCallback } from 'react';
import { LogIn, LogOut, Camera, CheckCircle, XCircle, Shield, Users, Vibrate } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { auth } from "@/lib/api";

const API = '/api';

export default function GateMobile() {
  const [user, setUser] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [tipoAccesso, setTipoAccesso] = useState('entrata');
  const tipoAccessoRef = useRef('entrata');
  useEffect(() => { tipoAccessoRef.current = tipoAccesso; }, [tipoAccesso]);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ entrate: 0, uscite: 0, presenti: 0 });
  const [lastScans, setLastScans] = useState([]);
  const html5QrRef = useRef(null);
  const autoRestartTimer = useRef(null);

  // Authenticate on mount
  useEffect(() => {
    auth.me()
      .then((userData) => {
        setUser(userData);
        setAuthenticated(true);
      })
      .catch(() => {
        window.location.href = '/login';
      });
  }, []);

  // Fetch stats periodically
  useEffect(() => {
    if (!authenticated) return;
    fetchStats();
    const interval = setInterval(fetchStats, 15000);
    return () => {
      clearInterval(interval);
      if (autoRestartTimer.current) clearTimeout(autoRestartTimer.current);
    };
  }, [authenticated]);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => { stopScanner(); };
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(API + '/reports/accessi', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStats({
          entrate: data.entrate_oggi || 0,
          uscite: data.uscite_oggi || 0,
          presenti: data.presenti_ora || 0
        });
      }
    } catch (e) { console.error(e); }
  };

  const startScanner = async () => {
    setError(null);
    setResult(null);
    setScanning(true);

    try {
      if (!html5QrRef.current) {
        html5QrRef.current = new Html5Qrcode('gate-scanner');
      }
      await html5QrRef.current.start(
        { facingMode: 'environment' },
        { fps: 15, qrbox: { width: 280, height: 280 }, aspectRatio: 1.0 },
        onScanSuccess,
        () => {}
      );
    } catch (e) {
      setError('Errore fotocamera: ' + e.message);
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrRef.current && html5QrRef.current.isScanning) {
      try {
        await html5QrRef.current.stop();
      } catch (e) { console.error(e); }
    }
    setScanning(false);
  };

  const vibrate = (pattern) => {
    if (navigator.vibrate) navigator.vibrate(pattern);
  };

  const onScanSuccess = async (decodedText) => {
    await stopScanner();
    vibrate([100, 50, 100]); // Double vibration on scan

    try {
      const res = await fetch(API + '/accessi', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          persona_id: decodedText,
          tipo_accesso: tipoAccessoRef.current,
          area_accesso: 'ingresso_principale'
        })
      });

      if (res.ok) {
        const data = await res.json();
        vibrate(200); // Success vibration
        const scanResult = {
          success: true,
          message: tipoAccessoRef.current === 'entrata' ? 'ENTRATA' : 'USCITA',
          persona: data.persona_nome || decodedText,
          time: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
        };
        setResult(scanResult);
        setLastScans(prev => [scanResult, ...prev].slice(0, 10));
        fetchStats();
      } else {
        const err = await res.json();
        vibrate([200, 100, 200, 100, 200]); // Error vibration
        setResult({ success: false, message: err.detail || 'Errore registrazione' });
      }
    } catch (e) {
      vibrate([200, 100, 200, 100, 200]);
      setResult({ success: false, message: 'Errore: ' + e.message });
    }

    // Auto-restart scanner after 3 seconds
    autoRestartTimer.current = setTimeout(() => {
      startScanner();
    }, 3000);
  };

  // Loading state
  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  // Check role
  const userRole = user?.role || 'user';
  if (userRole === 'user') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white p-4">
        <div className="text-center">
          <Shield className="h-16 w-16 mx-auto mb-4 text-red-400" />
          <h1 className="text-2xl font-bold mb-2">Accesso Negato</h1>
          <p className="text-slate-400">Non hai i permessi per accedere al Gate.</p>
          <a href="/" className="mt-4 inline-block text-blue-400 underline">Torna alla Dashboard</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white select-none" style={{ touchAction: 'manipulation' }}>
      {/* Header compatto */}
      <div className="bg-slate-800 px-4 py-2 flex items-center justify-between border-b border-slate-700">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-blue-400" />
          <span className="text-sm font-semibold">Gate ANC</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">{user?.name}</span>
          <a href="/" className="text-xs text-blue-400">Menu</a>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-0 bg-slate-800 border-b border-slate-700">
        <div className="text-center py-2 border-r border-slate-700">
          <div className="text-xl font-bold text-green-400">{stats.entrate}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Entrate</div>
        </div>
        <div className="text-center py-2 border-r border-slate-700">
          <div className="text-xl font-bold text-red-400">{stats.uscite}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Uscite</div>
        </div>
        <div className="text-center py-2">
          <div className="text-xl font-bold text-blue-400">{stats.presenti}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Presenti</div>
        </div>
      </div>

      {/* Tipo Accesso Toggle - GRANDE per touch */}
      <div className="grid grid-cols-2 gap-0">
        <button
          onClick={() => setTipoAccesso('entrata')}
          className={`py-4 text-lg font-bold transition-all ${
            tipoAccesso === 'entrata'
              ? 'bg-green-600 text-white shadow-lg shadow-green-600/30'
              : 'bg-slate-800 text-slate-400'
          }`}
        >
          <LogIn className="h-6 w-6 mx-auto mb-1" />
          ENTRATA
        </button>
        <button
          onClick={() => setTipoAccesso('uscita')}
          className={`py-4 text-lg font-bold transition-all ${
            tipoAccesso === 'uscita'
              ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
              : 'bg-slate-800 text-slate-400'
          }`}
        >
          <LogOut className="h-6 w-6 mx-auto mb-1" />
          USCITA
        </button>
      </div>

      {/* Scanner Area */}
      <div className="relative">
        <div id="gate-scanner" className="w-full bg-black" style={{ minHeight: '300px' }}></div>

        {!scanning && !result && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80">
            <button
              onClick={startScanner}
              className="flex flex-col items-center gap-3 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-12 py-6 rounded-2xl text-xl font-bold shadow-lg shadow-blue-600/30 transition-all active:scale-95"
            >
              <Camera className="h-10 w-10" />
              AVVIA SCANNER
            </button>
          </div>
        )}

        {scanning && (
          <button
            onClick={stopScanner}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-red-600/80 hover:bg-red-600 text-white px-6 py-2 rounded-full text-sm font-medium backdrop-blur"
          >
            Ferma
          </button>
        )}
      </div>

      {/* Result overlay */}
      {result && (
        <div className={`p-6 text-center ${
          result.success ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {result.success ? (
            <CheckCircle className="h-14 w-14 mx-auto mb-2" />
          ) : (
            <XCircle className="h-14 w-14 mx-auto mb-2" />
          )}
          <div className="text-2xl font-black">{result.message}</div>
          {result.persona && (
            <div className="text-lg mt-1 opacity-90">{result.persona}</div>
          )}
          <div className="text-xs mt-2 opacity-60">Prossima scansione automatica...</div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-300 p-4 mx-4 mt-4 rounded-lg text-center">
          {error}
          <button onClick={startScanner} className="block mx-auto mt-2 text-blue-400 underline text-sm">
            Riprova
          </button>
        </div>
      )}

      {/* Last scans log */}
      {lastScans.length > 0 && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-slate-400" />
            <span className="text-xs text-slate-400 uppercase tracking-wider">Ultime scansioni</span>
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {lastScans.map((scan, i) => (
              <div key={i} className="flex items-center justify-between bg-slate-800 rounded px-3 py-2 text-sm">
                <div className="flex items-center gap-2">
                  {scan.message === 'ENTRATA' ? (
                    <LogIn className="h-3 w-3 text-green-400" />
                  ) : (
                    <LogOut className="h-3 w-3 text-red-400" />
                  )}
                  <span className="text-slate-200">{scan.persona}</span>
                </div>
                <span className="text-xs text-slate-500">{scan.time}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
